import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsMcpServersOptions, getOrgAgentsToolsOptions } from '@onefootprint/fetch-dashboard';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import ExportPdfDialog, { useTenantLogo } from '@/components/pdf-export';
import { sanitizePdfFilename } from '@/utils/dom-to-pdf';
import { humanizeVendorKind, toolKeyForCatalogKind } from '@/utils/vendor-kind';
import { useBuilder } from '../../store/provider';
import { isBuiltinTool, isMcpTool, isVaultProxyTool } from '../../store/types';
import PolicyDocument from './policy-document';
type PolicyExportDialogProps = {
  open: boolean;
  onClose: () => void;
};
const PolicyExportDialog = t0 => {
  const $ = _c(41);
  const {
    open,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.exportDoc"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state
  } = useBuilder();
  const {
    bundle,
    session,
    current,
    latestRelease
  } = state;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getOrgAgentsToolsOptions();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = open && bundle.tools.length > 0;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    data: catalog
  } = useQuery(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getOrgAgentsMcpServersOptions();
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== bundle.tools || $[6] !== open) {
    t6 = open && bundle.tools.some(isMcpTool);
    $[5] = bundle.tools;
    $[6] = open;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = {
      ...t5,
      enabled: t6
    };
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const {
    data: mcpServers
  } = useQuery(t7);
  const {
    tenantName,
    tenantLogoSrc
  } = useTenantLogo(open);
  let t8;
  if ($[10] !== bundle.tools || $[11] !== catalog?.tools || $[12] !== mcpServers) {
    const displayNameByKey = new Map((catalog?.tools ?? []).map(_temp));
    t8 = bundle.tools.map(tool_0 => {
      if (isMcpTool(tool_0)) {
        return mcpServers?.find(s => s.id === tool_0.data.serverId)?.name ?? tool_0.data.serverId;
      }
      if (isVaultProxyTool(tool_0)) {
        return tool_0.data.name;
      }
      const key = isBuiltinTool(tool_0) ? tool_0.data.kind : tool_0.data.vendorKind;
      return displayNameByKey.get(key) ?? humanizeVendorKind(key);
    });
    $[10] = bundle.tools;
    $[11] = catalog?.tools;
    $[12] = mcpServers;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const toolNames = t8;
  const versionTag = current?.versionTag ?? session?.versionTag;
  let t9;
  if ($[14] !== latestRelease || $[15] !== t || $[16] !== versionTag) {
    t9 = () => {
      if (versionTag) {
        return t(_temp2, {
          version: versionTag
        });
      }
      if (latestRelease) {
        return t(_temp3, {
          version: latestRelease.latestVersion
        });
      }
      return t(_temp4);
    };
    $[14] = latestRelease;
    $[15] = t;
    $[16] = versionTag;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const getVersionLabel = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const generatedOn = t10;
  let t11;
  if ($[19] !== bundle.name || $[20] !== latestRelease || $[21] !== versionTag) {
    t11 = () => {
      const getVersion = () => {
        if (versionTag) {
          return `v${versionTag}`;
        }
        if (latestRelease) {
          return `v${latestRelease.latestVersion}-draft`;
        }
        return "draft";
      };
      return sanitizePdfFilename(`${format(new Date(), "yyyy_MM_dd")}_${bundle.name || "agent-policy"}_${getVersion()}`);
    };
    $[19] = bundle.name;
    $[20] = latestRelease;
    $[21] = versionTag;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const getFilename = t11;
  let t12;
  if ($[23] !== getFilename) {
    t12 = getFilename();
    $[23] = getFilename;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t) {
    t13 = t(_temp5);
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== getVersionLabel) {
    t14 = getVersionLabel();
    $[27] = getVersionLabel;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== bundle || $[30] !== t14 || $[31] !== tenantLogoSrc || $[32] !== tenantName || $[33] !== toolNames) {
    t15 = <PolicyDocument bundle={bundle} versionLabel={t14} generatedOn={generatedOn} toolNames={toolNames} tenantName={tenantName} tenantLogoSrc={tenantLogoSrc} />;
    $[29] = bundle;
    $[30] = t14;
    $[31] = tenantLogoSrc;
    $[32] = tenantName;
    $[33] = toolNames;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== onClose || $[36] !== open || $[37] !== t12 || $[38] !== t13 || $[39] !== t15) {
    t16 = <ExportPdfDialog filename={t12} onClose={onClose} open={open} title={t13}>{t15}</ExportPdfDialog>;
    $[35] = onClose;
    $[36] = open;
    $[37] = t12;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
};
export default PolicyExportDialog;
function _temp(tool) {
  return [toolKeyForCatalogKind(tool.kind), tool.displayName];
}
function _temp2($) {
  return $.meta.published;
}
function _temp3($_0) {
  return $_0.meta.draftWithRelease;
}
function _temp4($_1) {
  return $_1.meta.draft;
}
function _temp5($_2) {
  return $_2.dialog.title;
}